<template>
  <section ref="container" class="!relative flex h-full flex-col bg-primary !p-0 lg:bg-primary">
    <template v-if="!loading && value">
      <header
        v-if="showTitle && isLgAndUp"
        class="top-0 z-20 border-b border-primary bg-primary p-1 lg:sticky"
      >
        <UiTitle severity="h5" class="text-center">{{ value.title }}</UiTitle>
      </header>
      <div class="flex size-full flex-col lg:flex-row">
        <div
          class="sticky top-0 z-10 mb-2 flex h-fit flex-col bg-primary px-6 pt-12 lg:mb-0 lg:h-full lg:max-h-[calc(100vh_-_64px)] lg:w-1/2 lg:bg-primary lg:px-5 lg:py-7 lg:shadow-primary"
        >
          <header class="mb-4 lg:mb-3">
            <UiTitle severity="h3">
              {{ 'Выберите время' }}
            </UiTitle>
            <p v-if="showTitleOnVenue">{{ value.title }}</p>
            <UiRadioSelector
              v-if="seancesByVenue && seancesByVenue.length > 1"
              :model-value="filterOptions.find(({ value }) => value === filterMode)!"
              name="timetableFilter"
              :options="filterOptions"
              class="mt-4 lg:mt-3"
              @update:model-value="filterMode = $event.value"
            />
          </header>

          <template v-if="filterMode === 'venue'">
            <Suspense>
              <FilterSeancesPreviewByVenue v-model="activeVenue" :value="eventVenues" />

              <template #fallback>
                <UiLoader />
              </template>
            </Suspense>
          </template>

          <template v-else>
            <!--            табы дат-->
            <template v-if="!showCalendar && seancesByDayLessEightDays">
              <Suspense>
                <FilterSeancesPreviewByDay
                  v-if="isLgAndUp || (seancesByDay && seancesByDay?.length > 1)"
                  v-model="activeSeance as Seance['id'] | undefined"
                  :value="seancesByDayLessEightDays"
                  class="-mx-6 flex gap-2 px-6 lg:mx-0 lg:mb-0 lg:grid lg:grid-cols-2 lg:px-0"
                />
                <template #fallback>
                  <UiLoader />
                </template>
              </Suspense>
            </template>
            <!--            calendar-->
            <template v-else>
              <!--              desktop-->
              <template v-if="isLgAndUp">
                <UiDatePicker
                  class="scrollbar-hidden h-full !p-0"
                  range
                  show-reset
                  header-classes="mb-3 flex items-center justify-between"
                  :available-dates="availableForCalendarDates"
                  :model-value="calendarDates"
                  @update:model-value="onCalendarChange"
                />
              </template>
              <!--              mobile-->
              <template v-else>
                <header class="flex w-full">
                  <UiDatePickerButton
                    v-if="showCalendar"
                    :value="calendarDates"
                    :class="{ 'min-w-max px-5': value }"
                    class="mr-2 min-w-[46px] rounded-full bg-primary !px-3 py-3"
                    @click="mobileCalendarIsOpen = true"
                    @reset="activeSeance = undefined"
                  />
                  <Suspense>
                    <FilterSeancesPreviewByDay
                      v-if="seancesByDayLessEightDays"
                      v-model="activeSeance as Seance['id'] | undefined"
                      :value="seancesByDayLessEightDays"
                      class="flex gap-2"
                    />
                    <template #fallback>
                      <UiLoader />
                    </template>
                  </Suspense>
                </header>

                <UiDialog
                  v-model="mobileCalendarIsOpen"
                  swipe-close
                  save-scroll-lock
                  position="bottom"
                >
                  <header>
                    <UiButton
                      icon-transparent
                      class="!absolute right-4 top-7 z-20 flex size-7 min-w-7 items-center justify-center"
                      @click="mobileCalendarIsOpen = false"
                    >
                      <UiIcon name="x-mark" class="size-4.5 text-text-main lg:text-light" />
                    </UiButton>
                  </header>
                  <UiDatePicker
                    class="scrollbar-hidden z-10 !h-[calc(100dvh_-_16px)] rounded-t-3xl !p-0 !px-5"
                    :model-value="calendarDates"
                    range
                    :available-dates="availableForCalendarDates"
                    need-confirm
                    show-reset
                    header-classes="mb-3 flex px-0 pt-6"
                    @update:model-value="onCalendarChange"
                  >
                    <template #header>
                      <div>
                        <UiTitle severity="h2">Календарь</UiTitle>
                      </div>
                    </template>
                    <template #confirm-text>Показать сеансы</template>
                  </UiDatePicker>
                </UiDialog>
              </template>
            </template>
          </template>
        </div>
        <div
          class="scrollbar-hidden relative z-20 h-full overflow-y-scroll px-4 pb-4.5 pt-8 lg:w-1/2 lg:border-l lg:border-fivefold lg:p-7"
        >
          <Suspense>
            <LookEventSeances
              v-if="filteredSeances"
              :value="filteredSeances"
              :mode="filterMode"
              class=""
            />

            <template #fallback>
              <UiLoader class="size-full" />
            </template>
          </Suspense>
        </div>
      </div>
    </template>
    <template v-else>
      <EventTimetableSkeleton />
    </template>
  </section>
</template>

<script lang="ts" setup>
import { isAfter, isSameDay, isBefore } from 'date-fns'
import type { Undefinable } from 'ts-helpers'
import { computed, defineAsyncComponent, onMounted, ref, shallowRef, watch } from 'vue'
import { useRoute } from 'vue-router'
import type { Event } from '@/5_entities/Event'
import { getSeancesByDay, getSeancesByVenue, type Seance } from '@/5_entities/Seance'
import type { Venue } from '@/5_entities/Venue'
import { useBreakpoint } from '@/6_shared/lib'
import type { TimetableFilterMode } from '@/6_shared/model'
import {
  UiButton,
  UiDatePicker,
  UiDialog,
  UiIcon,
  UiTitle,
  UiDatePickerButton,
  UiRadioSelector,
  UiLoader
} from '@/6_shared/ui'
import EventTimetableSkeleton from './EventTimetableSkeleton.vue'

type PropType = {
  value?: Event
  loading?: boolean
  showTitle?: boolean
  activeDate?: string
  showTitleOnVenue?: boolean
}

const props = withDefaults(defineProps<PropType>(), {
  loading: false,
  value: undefined,
  showTitle: false,
  activeDate: undefined,
  showTitleOnVenue: false
})

const FilterSeancesPreviewByDay = defineAsyncComponent(async () => {
  const { FilterSeancesPreviewByDay } = await import('@/4_features/Event')

  return FilterSeancesPreviewByDay
})
const LookEventSeances = defineAsyncComponent(async () => {
  const { LookEventSeances } = await import('@/4_features/Event')

  return LookEventSeances
})
const FilterSeancesPreviewByVenue = defineAsyncComponent(async () => {
  const { FilterSeancesPreviewByVenue } = await import('@/4_features/Event')

  return FilterSeancesPreviewByVenue
})

const {
  lg: [isLgAndUp]
} = useBreakpoint()

const route = useRoute()
const filterMode = ref<TimetableFilterMode>(
  (route.query.timetable as TimetableFilterMode) || 'date'
)
const filterOptions: { title: string; value: TimetableFilterMode }[] = [
  { title: 'по дате', value: 'date' },
  { title: 'по площадкам', value: 'venue' }
]
const activeVenue = shallowRef<Undefinable<Venue['id']>>(undefined)
const seancesByVenue = computed(() => props.value && getSeancesByVenue(props.value.seances))
const eventVenues = computed(() => {
  return (
    props.value?.seances.reduce((acc, item) => {
      if (!item.venue) return acc

      if (!acc.find((venue) => venue.id === item.venue!.id)) acc.push(item.venue)

      return acc
    }, [] as Venue[]) ?? []
  )
})

// old
const MAX_VISIBLE_DAYS_DESKTOP = 8
const MAX_VISIBLE_DAYS_MOBILE = 3
const maxVisibleDays = computed(() =>
  isLgAndUp.value ? MAX_VISIBLE_DAYS_DESKTOP : MAX_VISIBLE_DAYS_MOBILE
)

const activeSeance = shallowRef<Undefinable<Seance['id'] | Seance['id'][]>>(undefined)
const mobileCalendarIsOpen = ref(false)

/**
 * даты отображения в мобильном календаре
 */
const calendarDates = computed((): Undefinable<Date | Date[]> => {
  if (!activeSeance.value || !props.value) return

  if (Array.isArray(activeSeance.value))
    return [
      new Date(props.value.findSeanceById(activeSeance.value[0])!.beginsAt),
      new Date(props.value.findSeanceById(activeSeance.value[1])!.beginsAt)
    ]

  return new Date(props.value.findSeanceById(activeSeance.value)!.beginsAt)
})
/**
 * все сеансы, сгруппированные по дням
 */
const seancesByDay = computed(() => props.value && getSeancesByDay(props.value.seances))
/**
 * отображать ли календарь
 */
const showCalendar = computed(
  () => seancesByDay.value && seancesByDay.value.length > maxVisibleDays.value
)
/**
 * сенасы по дням, но < 8 дней
 */
const seancesByDayLessEightDays = computed(
  () =>
    !!seancesByDay.value &&
    seancesByDay.value.filter((_item, index) => index < maxVisibleDays.value)
)
/**
 * отфильтрованные по выбранному дню сеансы
 */
const filteredSeancesByDate = computed(() => {
  const findSeance = (seanceId: Seance['id']) => props.value && props.value.findSeanceById(seanceId)
  const filterSeancesFoLessEightDays = () =>
    activeSeance.value
      ? [seancesByDay.value!.find(([seance]) => seance.id === activeSeance.value)!]
      : seancesByDayLessEightDays.value
  const filterSeancesForCalendar = () => {
    const findSeancesForRange = (beginsAt: Seance['beginsAt']) => {
      const [firsSeanceId, secondSeanceId] = activeSeance.value as Seance['id'][]
      const firstSeance = findSeance(firsSeanceId)
      const secondSeance = findSeance(secondSeanceId)

      return (
        (isSameDay(firstSeance!.beginsAt, beginsAt) || isAfter(beginsAt, firstSeance!.beginsAt)) &&
        (isSameDay(secondSeance!.beginsAt, beginsAt) || isBefore(beginsAt, secondSeance!.beginsAt))
      )
    }

    return activeSeance.value
      ? seancesByDay.value!.filter(([seance]) => {
          const { beginsAt } = seance

          if (Array.isArray(activeSeance.value)) return findSeancesForRange(beginsAt)

          return isSameDay(beginsAt, findSeance(activeSeance.value!)!.beginsAt)
        })
      : seancesByDay.value
  }

  if (!props.value) return

  if (!showCalendar.value) {
    return filterSeancesFoLessEightDays()
  }

  return filterSeancesForCalendar()
})
/**
 * отфильтрованные по выбранной площадке
 */
const filteredSeancesByVenue = computed(() => {
  if (!seancesByVenue.value) return

  return activeVenue.value
    ? [seancesByVenue.value.find(([seance]) => seance.venue?.id === activeVenue.value)!]
    : seancesByVenue.value
})
/**
 * сеансы для показа
 */
const filteredSeances = computed(() => {
  return filterMode.value === 'date' ? filteredSeancesByDate.value : filteredSeancesByVenue.value
})
const findSeanceId = (date: Date) =>
  props.value && props.value.seances.find((item) => isSameDay(date, item.beginsAt))?.id
/**
 * выбор даты в календаре
 * @param value
 */
const onCalendarChange = (value: Undefinable<Date | Date[]>) => {
  mobileCalendarIsOpen.value = false

  if (!props.value) return
  if (!value) return (activeSeance.value = undefined)

  if (Array.isArray(value)) {
    const [start, end] = value
    const firstSeance = findSeanceId(start)
    const lastSeance = findSeanceId(end)

    activeSeance.value =
      firstSeance && lastSeance ? [firstSeance, lastSeance] : firstSeance || lastSeance

    return
  }

  activeSeance.value = findSeanceId(value)
}
/**
 * только активные даты для календаря
 */
const availableForCalendarDates = computed(() => {
  if (!showCalendar.value || !seancesByDay.value) return

  return seancesByDay.value.map(([seance]) => new Date(seance.beginsAt))
})

/**
 * скроллить окно наверх при выборе даты
 */
const container = ref<HTMLElement | null>(null)
const scrollContainerToTop = () => {
  if (!container.value) return

  container.value.scrollTop = 0
}
watch(() => activeSeance.value, scrollContainerToTop)

const setActiveSeanceFromActiveDate = (val?: Event) => {
  if (val && props.activeDate) {
    activeSeance.value = findSeanceId(new Date(props.activeDate))
  }
}

watch(
  () => props.value,
  (val) => setActiveSeanceFromActiveDate(val)
)
onMounted(() => {
  setActiveSeanceFromActiveDate(props.value)
})
</script>
